'use client';
import { detachPromptHistory } from '#pplx/frontend/composer-sdk-plugins/prompt-history/promptHistoryMechanics.js';
import { EMPTY_PROMPT_HISTORY_STATE, acceptPromptHistorySubmission, navigatePromptHistory, preparePromptHistory, } from '#pplx/frontend/composer-sdk-plugins/prompt-history/promptHistoryState.js';
import { createPluginFactory } from '#pplx/frontend/composer-sdk/plugins/createPluginFactory.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
function getPromptHistoryDirection(key) {
    if (key === 'ArrowUp')
        return 'up';
    if (key === 'ArrowDown')
        return 'down';
    return undefined;
}
const promptHistoryPluginFactory = createPluginFactory({
    name: 'prompt-history',
    capabilities: [
        {
            kind: 'keyboard',
            configure: (props) => ({
                disabled: props.disabled,
                onKeyDown: props.onKeyDown,
                onUserEdit: props.onUserEdit,
            }),
        },
    ],
});
export function usePromptHistoryPlugin({ history, disabled = false, resetKey, onEvent, }) {
    const preparedHistory = useMemo(() => preparePromptHistory(history), [history]);
    const historyRef = useRef(preparedHistory);
    const onEventRef = useRef(onEvent);
    const stateRef = useRef(EMPTY_PROMPT_HISTORY_STATE);
    historyRef.current = preparedHistory;
    onEventRef.current = onEvent;
    const reset = useCallback(() => {
        stateRef.current = EMPTY_PROMPT_HISTORY_STATE;
    }, []);
    useEffect(() => {
        reset();
    }, [reset, resetKey]);
    const handleKeyDown = useCallback((event, context) => {
        const direction = getPromptHistoryDirection(event.key);
        if (direction === undefined)
            return false;
        const result = navigatePromptHistory({
            currentValue: context.value,
            direction,
            history: historyRef.current,
            state: stateRef.current,
        });
        stateRef.current = result.state;
        if (!result.handled || result.replacement === undefined)
            return false;
        context.replaceValue(result.replacement);
        if (result.event !== undefined)
            onEventRef.current?.(result.event);
        return true;
    }, []);
    const handleUserEdit = useCallback(() => {
        const result = detachPromptHistory(stateRef.current, historyRef.current.length);
        stateRef.current = result.state;
        if (result.event !== undefined)
            onEventRef.current?.(result.event);
    }, []);
    const acceptSubmission = useCallback((value) => {
        const result = acceptPromptHistorySubmission(stateRef.current, historyRef.current.length, value);
        stateRef.current = result.state;
        if (result.event !== undefined)
            onEventRef.current?.(result.event);
    }, []);
    const plugin = useMemo(() => promptHistoryPluginFactory({
        disabled,
        onKeyDown: handleKeyDown,
        onUserEdit: handleUserEdit,
    }), [disabled, handleKeyDown, handleUserEdit]);
    return useMemo(() => ({ plugin, acceptSubmission }), [acceptSubmission, plugin]);
}
//# sourceMappingURL=usePromptHistoryPlugin.js.map